/**
 * THE DOCUMENT, AS THE EDITOR HOLDS IT — and the client-side mirror of all eight of D21's rules.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★★ WHY THERE IS NO TEXTAREA HERE, AND WHY THE PROTOTYPE'S ONE CANNOT BE PORTED
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * The wire format is a TYPED, VERSIONED BLOCK UNION — text / quote / image / divider — and **every
 * block carries a stable id given at creation that survives across versions of that block**, which
 * is what the review anchors point at (authors-api.yaml's `Block`; `00-scope-v1.md:177-179` kills
 * `rawHTML`). A plain textarea produces one string with no ids, so a document round-tripped through
 * one loses every anchor in the article, silently and with no failing test. `docs/ui/04-pages.md:457`
 * records the same finding against the prototype.
 *
 * ★ AND THE IDS ARE UUIDv7, NOT ANY OPAQUE STRING. `PublicId` is a loose string — four shapes are
 * live (shared.yaml:229-237) — but `blockId` specifically is checked against
 * `ck_awb_id_format`, a LOWERCASE UUIDv7 (`shared/content-model/blocks.ts:80-82`). So a new block
 * is minted through the one generator, never with `crypto.randomUUID()`, which is a v4 and would be
 * refused at the boundary.
 *
 * ★★ AND THE GENERATOR IS GIVEN AN INSTANT RATHER THAN READING ONE. `eslint.config.mjs:41-60` bans
 * zero-argument `new Date()` and `Date.now()` across `web/` (C11), and `uuidv7` takes a `Date`
 * precisely so a caller supplies one. What this screen supplies is the SERVER's own
 * `WorkingCopy.updatedAt` — parsing an instant you were given is explicitly not a clock read
 * (the rule's own message says so) — and the generator's monotonic counter keeps every id inside
 * one millisecond distinct and increasing (`ids.ts:58-59`). So a burst of new blocks between two
 * saves gets distinct ids without this app ever learning what time it is.
 *
 * DESIGN 15 (the owner's plain-words rule, 2026-10-01): a row this file cannot check says so in the
 * author's words - "it is checked when you submit the article" - not "the platform sent no bound".
 */
import { uuidv7 } from '../../../../../../shared/content-model/ids';
import { isApiProblem } from '../../../features/cabinet/api/problem';
import { i18n } from '../../../i18n';
import { articlesT } from '../articles-i18n';
import type { ArticlesT } from '../articles-i18n';
import { isSlugRefusal } from './address';
import type { PieceContext } from './piece-context';
import {
  articleWordCount,
  blockAttribution,
  blockText,
  countTextLength,
  countTitleLength,
} from '../../../features/cabinet/api/editor-measure';
import type {
  ArticleVideo,
  Block,
  BlockKind,
  StructuralRule,
  StructuralRuleName,
  StructuralRules,
  WorkingCopy,
} from '../../../features/cabinet/api/editor-api';

/**
 * The editable half of a working copy. Everything the author can change and nothing the server
 * owns — no `revision`, no `wordCount`, no `writableBy`: those move only when a save is accepted,
 * and holding a copy of them here is how two sources of truth appear.
 */
export interface EditorDraft {
  readonly title: string;
  readonly subtitle: string;
  readonly seoTitle: string;
  readonly metaDescription: string;
  readonly slug: string;
  /** The language the author writes in - a code, `null` only where the server sent none. */
  readonly sourceLanguage: string | null;
  readonly articleFormat: string;
  readonly timeSensitivity: string;
  readonly writtenWithAi: boolean;
  readonly plannedGoLiveAt: string | null;
  readonly categories: readonly string[];
  readonly addressCategory: string | null;
  readonly tags: readonly string[];
  readonly blocks: readonly Block[];
  /** The cover's media id. The rendition beside it is read-only and derived by the platform. */
  readonly coverFileId: string | null;
}

/** Everything that is NOT the draft: the server's own members, kept apart on purpose. */
export interface DraftBase {
  readonly articleId: string;
  readonly revision: number;
  readonly status: WorkingCopy['status'];
  readonly writableBy: NonNullable<WorkingCopy['writableBy']>;
  readonly updatedAt: string;
  /** The source language the platform HOLDS - what a save that sends none leaves in place. */
  readonly savedSourceLanguage: string | null;
  /** The web address the platform HOLDS (R-019) - what a save that sends none keeps. */
  readonly savedSlug: string | null;
  readonly awaitingCover: boolean;
  readonly coverUrl: string | null;
  readonly coverWidthPx: number | null;
  readonly coverHeightPx: number | null;
}

export function draftOf(copy: WorkingCopy): EditorDraft {
  return {
    title: copy.title,
    subtitle: copy.subtitle,
    // `null` and `''` are the same thing to an author and two different things on the wire. The
    // form holds strings; ./use-editor-draft.ts turns an empty one back into null at the boundary.
    seoTitle: copy.seoTitle ?? '',
    metaDescription: copy.metaDescription ?? '',
    slug: copy.slug ?? '',
    sourceLanguage: copy.sourceLanguage ?? null,
    articleFormat: copy.articleFormat ?? '',
    timeSensitivity: copy.timeSensitivity ?? '',
    writtenWithAi: copy.writtenWithAi ?? false,
    plannedGoLiveAt: copy.plannedGoLiveAt ?? null,
    categories: copy.categories ?? [],
    addressCategory: copy.addressCategory ?? null,
    tags: copy.tags ?? [],
    blocks: copy.blocks,
    coverFileId: copy.cover?.fileId ?? null,
  };
}

export function baseOf(copy: WorkingCopy): DraftBase {
  return {
    articleId: copy.articleId,
    revision: copy.revision,
    status: copy.status,
    // The default is the SAFE one. An absent `writableBy` means the server did not say, and
    // assuming "author" there would let the screen offer a write it cannot have.
    writableBy: copy.writableBy ?? 'nobody',
    updatedAt: copy.updatedAt,
    savedSourceLanguage: copy.sourceLanguage ?? null,
    savedSlug: copy.slug ?? null,
    awaitingCover: copy.awaitingCover ?? false,
    coverUrl: copy.cover?.url ?? null,
    coverWidthPx: copy.cover?.widthPx ?? null,
    coverHeightPx: copy.cover?.heightPx ?? null,
  };
}

/* ---------------------------------------------------------------------------------------------
 * Blocks.
 * ------------------------------------------------------------------------------------------- */

/** See the header: the instant comes from the server's last `updatedAt`, never from this machine. */
export function newBlockId(serverInstant: string): string {
  const parsed = new Date(serverInstant);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error(
      `newBlockId: "${serverInstant}" is not an instant. It must be the server's own ` +
        'WorkingCopy.updatedAt — this app has no clock of its own (C11, eslint.config.mjs:41-60).',
    );
  }
  return uuidv7(parsed);
}

export function newBlock(kind: BlockKind, serverInstant: string): Block {
  const blockId = newBlockId(serverInstant);
  switch (kind) {
    case 'text':
      return { blockId, kind: 'text', body: '' };
    case 'quote':
      return { blockId, kind: 'quote', body: '', quoteAttribution: null };
    case 'divider':
      return { blockId, kind: 'divider' };
    case 'image':
      // AN IMAGE BLOCK NEEDS A CLAIMED `fileId` AND CANNOT BE MADE EMPTY. "Add an image" in
      // ./BlockList.tsx writes the file and claims it first, then builds the block with
      // `imageBlock` below from what `claimArticleImageUpload` answered.
      throw new Error(
        'newBlock: an image block is created from a claimed upload (claimArticleImageUpload), ' +
          'because ImageBlock.fileId is required and the platform mints it.',
      );
    case 'video':
      // A video block is added once `setArticleVideoBlock` has set its video (./VideoBlockDialog.tsx):
      // the platform refuses a save naming a video block with no video.
      throw new Error('newBlock: a video block is created from setArticleVideoBlock.');
    default:
      throw new Error(`newBlock: unknown block kind ${String(kind)}`);
  }
}

export function imageBlock(
  blockId: string,
  fileId: string,
  url: string | undefined,
  alt: string | null,
): Block {
  return {
    blockId,
    kind: 'image',
    fileId,
    // `url` is declared read-only and derived by the platform; it is carried when the claim gave
    // one so the block can render, and omitted otherwise rather than invented.
    ...(url === undefined ? {} : { url }),
    imageAlt: alt,
    caption: null,
  };
}

/** A video block, once `setArticleVideoBlock` has set its video under this id. */
export function videoBlock(blockId: string, video: ArticleVideo): Block {
  return { blockId, kind: 'video', video };
}

/** Replace one block by id, keeping order. The id is what survives; the position is not. */
export function replaceBlock(
  blocks: readonly Block[],
  blockId: string,
  next: (block: Block) => Block,
): readonly Block[] {
  return blocks.map((block) => (block.blockId === blockId ? next(block) : block));
}

export function removeBlock(blocks: readonly Block[], blockId: string): readonly Block[] {
  return blocks.filter((block) => block.blockId !== blockId);
}

/**
 * Move one block by one position.
 *
 * ★ A PURE REORDER IS A REAL EDIT AND MUST BE SAVED. The save path compares the fingerprint AND
 * the ordered list of block ids, "otherwise an author who only reorders their paragraphs and
 * presses save has the save silently discarded with no error" (`WC-CR-07`,
 * authors-api.yaml:1044-1047). Nothing in this file may dedupe a reorder away.
 */
export function moveBlock(
  blocks: readonly Block[],
  blockId: string,
  direction: -1 | 1,
): readonly Block[] {
  const from = blocks.findIndex((block) => block.blockId === blockId);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= blocks.length) {
    return blocks;
  }
  const next = [...blocks];
  const moved = next[from];
  const displaced = next[to];
  if (moved === undefined || displaced === undefined) {
    return blocks;
  }
  next[from] = displaced;
  next[to] = moved;
  return next;
}

/* ---------------------------------------------------------------------------------------------
 * The checklist — the client mirror of all eight of D21's rules (adr/0008:42).
 * ------------------------------------------------------------------------------------------- */

export type ChecklistState = 'ok' | 'failing' | 'unknowable';

/**
 * The one row the editor adds of its OWN, beside D21's eight (design 14, F8): the web address
 * category, ruling 8(a). It is not a structural rule - no bound, no policy version, its own refusal
 * (`ADDRESS_CATEGORY_REQUIRED`) - so it is never built here; ./address.ts builds it.
 */
export type EditorRuleName = 'address_category' | VideoRuleName;

/**
 * A video's own rows (v1.0.29, ../../videos/video-checklist.ts): the video is ready, and every rule
 * of the video guidelines is confirmed - or, for a live stream (1.2.0), its planned times are set.
 * Like the address row, none is a structural rule.
 */
export type VideoRuleName = 'video_ready' | 'video_rights' | 'stream_times';

export interface ChecklistRow {
  readonly rule: StructuralRuleName | EditorRuleName;
  /** The server's own wording where it sent one; this file's fallback where it did not. */
  readonly label: string;
  readonly state: ChecklistState;
  /** What the draft currently measures, in the rule's own unit. Null where it is not a count. */
  readonly actual: number | null;
  readonly min: number | null;
  readonly max: number | null;
  /** One sentence the author can act on. */
  readonly detail: string;
}

/**
 * ★★ WHAT THIS LIST IS NOT. The prototype draws SEVEN checklist items; the platform rules EIGHT,
 * and they are not the same seven. Three of the prototype's are wrong in a way worth naming:
 * item 5 can never fail, item 6 is hardcoded true, and **item 7 — "no medical or legal claims" —
 * is a moderation judgement the author must never be shown**: "No number from the moderation
 * pipeline appears here" (authors-api.yaml:1817, restated at :4907). It is not rendered, not
 * approximated, and not replaced by a softer version of itself.
 *
 * ★ EVERY BOUND BELOW COMES OFF `getStructuralRules`. Not one number is written into this file.
 * That is the operation's whole purpose — "the editor's client-side mirror renders these, so client
 * and server cannot drift" (authors-api.yaml:5144) — and it is why the prototype's hardcoded 600 is
 * not merely wrong but unrepresentable here.
 *
 * ★ A RULE WHOSE BOUNDS ARE ALL NULL IS `unknowable`, NOT `ok`. A policy that sent no numbers for a
 * rule has told the client nothing, and a green tick for "we could not check" is the failure mode
 * this whole screen exists to avoid.
 */
export function checklistFor(
  draft: EditorDraft,
  base: DraftBase,
  rules: StructuralRules,
  t: ArticlesT = articlesT(),
  context: PieceContext = '',
): readonly ChecklistRow[] {
  return rules.rules.map((rule) => evaluateRule(rule, draft, base, t, context));
}

function band(
  rule: StructuralRule,
  actual: number,
  noun: CountedNoun,
  t: ArticlesT,
  context: PieceContext,
): { state: ChecklistState; detail: string } {
  const { min, max } = rule;
  const have = counted(actual, noun, t);
  if ((min === undefined || min === null) && (max === undefined || max === null)) {
    return {
      state: 'unknowable',
      // Deliberately not a tick. See `checklistFor`'s header.
      detail: t('editor.checklist.cannotCheckYouHave', { counted: have, context }),
    };
  }
  if (min !== undefined && min !== null && actual < min) {
    return {
      state: 'failing',
      detail: t('editor.checklist.atLeast', { counted: have, count: min }),
    };
  }
  if (max !== undefined && max !== null && actual > max) {
    return {
      state: 'failing',
      detail: t('editor.checklist.limit', { counted: have, max: String(max) }),
    };
  }
  return { state: 'ok', detail: t('editor.checklist.okCount', { counted: have }) };
}

function evaluateRule(
  rule: StructuralRule,
  draft: EditorDraft,
  base: DraftBase,
  t: ArticlesT,
  context: PieceContext,
): ChecklistRow {
  // The fallback wording, used only where `StructuralRule.message` is absent.
  const label = rule.message ?? t(`editor.checklist.rules.${rule.rule}`);
  const min = rule.min ?? null;
  const max = rule.max ?? null;

  switch (rule.rule) {
    case 'word_count': {
      // THE server-side definition, through the one shared function. See editor-measure.ts.
      const actual = articleWordCount(draft);
      return {
        rule: rule.rule,
        label,
        min,
        max,
        actual,
        ...band(rule, actual, 'words', t, context),
      };
    }
    case 'block_count': {
      const actual = draft.blocks.length;
      return {
        rule: rule.rule,
        label,
        min,
        max,
        actual,
        ...band(rule, actual, 'blocks', t, context),
      };
    }
    case 'title_length': {
      // CODE POINTS, NFC-normalised, trailing spaces left out — the same measurement the submit
      // guard makes (`countTitleLength`). `.length` is UTF-16 units and would over-count an emoji.
      const actual = countTitleLength(draft.title);
      return {
        rule: rule.rule,
        label,
        min,
        max,
        actual,
        ...band(rule, actual, 'characters', t, context),
      };
    }
    case 'subtitle_length': {
      const actual = countTitleLength(draft.subtitle);
      return {
        rule: rule.rule,
        label,
        min,
        max,
        actual,
        ...band(rule, actual, 'characters', t, context),
      };
    }
    case 'category_count': {
      const actual = draft.categories.length;
      return {
        rule: rule.rule,
        label,
        min,
        max,
        actual,
        ...band(rule, actual, 'categories', t, context),
      };
    }
    case 'image_present': {
      // "≥1 image INCLUDING THE COVER" (docs/design/09-authors-app.md:296), so a piece whose only
      // picture is its cover passes and a piece with neither does not.
      const actual =
        (draft.coverFileId === null ? 0 : 1) +
        draft.blocks.filter((block) => block.kind === 'image').length;
      return {
        rule: rule.rule,
        label,
        min,
        max,
        actual,
        state: actual >= (min ?? 1) ? 'ok' : 'failing',
        detail:
          actual > 0
            ? t('editor.checklist.imagesWithCover', { counted: counted(actual, 'images', t) })
            : t('editor.checklist.noImage'),
      };
    }
    case 'quote_length': {
      /* ★ TWO BOUNDS UNDER ONE RULE NAME, and both are measured over the LONGEST quote in the
         article. `bodyMax`/`attributionMax` landed on 2026-09-20; before they existed the two
         columns were collapsed into a single `max`, and `quote_length` was the one rule of the
         eight no client could check before submitting. Reading only `max` here would silently
         restore that gap for the attribution. */
      const bodyMax = rule.bodyMax ?? rule.max ?? null;
      const attributionMax = rule.attributionMax ?? null;
      const quotes = draft.blocks.filter((block) => block.kind === 'quote');
      const longestBody = quotes.reduce(
        (worst, block) => Math.max(worst, countTextLength(blockText(block) ?? '')),
        0,
      );
      const longestAttribution = quotes.reduce(
        (worst, block) => Math.max(worst, countTextLength(blockAttribution(block) ?? '')),
        0,
      );
      if (quotes.length === 0) {
        return {
          rule: rule.rule,
          label,
          min,
          max: bodyMax,
          actual: 0,
          state: 'ok',
          detail: t('editor.checklist.noQuotes', { context }),
        };
      }
      if (bodyMax === null && attributionMax === null) {
        return {
          rule: rule.rule,
          label,
          min,
          max: null,
          actual: longestBody,
          state: 'unknowable',
          detail: t('editor.checklist.cannotCheck', { context }),
        };
      }
      const bodyOver = bodyMax !== null && longestBody > bodyMax;
      const attributionOver = attributionMax !== null && longestAttribution > attributionMax;
      return {
        rule: rule.rule,
        label,
        min,
        max: bodyMax,
        actual: longestBody,
        state: bodyOver || attributionOver ? 'failing' : 'ok',
        detail: t('editor.checklist.quotes', {
          body: ofMax(longestBody, bodyMax, t),
          attribution: ofMax(longestAttribution, attributionMax, t),
        }),
      };
    }
    case 'cover_dimensions': {
      const minWidth = rule.minWidthPx ?? null;
      const minHeight = rule.minHeightPx ?? null;
      // Asked only where the author can still change the cover (the owner, 2026-10-05).
      if (base.writableBy !== 'author') {
        return coverAsItStands(rule.rule, label, minWidth, draft.coverFileId !== null, base, t);
      }
      if (draft.coverFileId === null) {
        return {
          rule: rule.rule,
          label,
          min: minWidth,
          max: null,
          actual: null,
          state: 'failing',
          detail:
            minWidth === null || minHeight === null
              ? t('editor.checklist.noCoverYet')
              : t('editor.checklist.noCoverYetNeeds', {
                  width: String(minWidth),
                  height: String(minHeight),
                }),
        };
      }
      if (minWidth === null || minHeight === null) {
        return {
          rule: rule.rule,
          label,
          min: null,
          max: null,
          actual: base.coverWidthPx,
          state: 'unknowable',
          detail: t('editor.checklist.cannotCheck', { context }),
        };
      }
      /* `awaitingCover` IS THE SERVER'S OWN DERIVED FLAG for "no cover yet meets the ruled
         minimum" (D17), and it is authoritative when the two disagree: the dimensions on the
         stored image are what the platform measured, and the flag is what it concluded. */
      const width = base.coverWidthPx ?? 0;
      const height = base.coverHeightPx ?? 0;
      const small = base.awaitingCover || width < minWidth || height < minHeight;
      return {
        rule: rule.rule,
        label,
        min: minWidth,
        max: null,
        actual: width,
        state: small ? 'failing' : 'ok',
        detail: small
          ? t('editor.checklist.coverTooSmall', {
              width: String(width),
              height: String(height),
              minWidth: String(minWidth),
              minHeight: String(minHeight),
            })
          : t('editor.checklist.coverSize', { width: String(width), height: String(height) }),
      };
    }
    default:
      // `StructuralRuleName` is closed; a ninth rule upstream becomes a compile error here rather
      // than a row that quietly renders as a tick.
      return {
        rule: rule.rule,
        label,
        min,
        max,
        actual: null,
        state: 'unknowable',
        detail: t('editor.checklist.cannotCheckYet', { context }),
      };
  }
}

/** How many rows are red. The Submit button reads this and says the number out loud. */
export function failingCount(rows: readonly ChecklistRow[]): number {
  return rows.filter((row) => row.state === 'failing').length;
}

/* ---------------------------------------------------------------------------------------------
 * The source language (register R-003).
 * ------------------------------------------------------------------------------------------- */

/**
 * ★★ THE LANGUAGES AN ARTICLE MAY BE WRITTEN IN ARE SERVED, NOT WRITTEN HERE (wave 26).
 * "Source languages v1: EN / RU / PL / ES ... The supported set is published to authors"
 * (00-scope-v1.md, D24.1). The platform holds the set as `translation_policy_source_language` and
 * serves it on `listSourceLanguages` - the very set the autosave checks a change against - so the
 * Setup pane reads it (./SetupPanel.tsx) and this file only turns a served entry into words. A drift
 * between the read and a save is still not silent: a code the policy stops offering meanwhile is the
 * autosave's uncoded 422, which the editor answers by putting the stored language back
 * (./use-editor-draft.ts).
 *
 * THE LABEL IS THE PLATFORM'S NAME WITH THE LANGUAGE'S OWN NAME BESIDE IT, so an author finds
 * theirs: "Russian (Русский)". The own name comes from the browser's locale data
 * (`Intl.DisplayNames`), because the catalogue holds one name per language and it is English
 * (`language.label_en`); where the browser knows none, or it is the same word, the platform's name
 * stands alone. In a cabinet shown in another language (design 29, W5) the first name is the
 * browser's for the cabinet's language - "Russisch (Русский)" in German - and the platform's English
 * one only where the browser has none.
 */

/** One served language - `listSourceLanguages`' `items[]`, which this shape is. */
export interface OfferedLanguage {
  readonly code: string;
  readonly name: string;
}

/** A language's own name for itself, capitalised as a label is ("русский" -> "Русский"), or null. */
function ownName(code: string): string | null {
  try {
    const own = new Intl.DisplayNames([code], { type: 'language', fallback: 'none' }).of(code);
    if (own === undefined || own === '') {
      return null;
    }
    const [first = '', ...rest] = [...own];
    return `${first.toLocaleUpperCase(code)}${rest.join('')}`;
  } catch {
    // Not a tag this browser can read: the label falls back to what the platform said.
    return null;
  }
}

/** The browser's name for a code in `language`, capitalised as a label is, or null. */
function nameIn(code: string, language: string): string | null {
  try {
    const named = new Intl.DisplayNames([language], { type: 'language', fallback: 'none' }).of(
      code,
    );
    if (named === undefined || named === '') {
      return null;
    }
    const [first = '', ...rest] = [...named];
    return `${first.toLocaleUpperCase(language)}${rest.join('')}`;
  } catch {
    return null;
  }
}

/** The browser's English name for a code the platform did not name, or null. */
function englishName(code: string): string | null {
  try {
    return new Intl.DisplayNames(['en'], { type: 'language', fallback: 'none' }).of(code) ?? null;
  } catch {
    return null;
  }
}

function languageLabel(code: string, name: string | null, t: ArticlesT): string {
  // Read at the call: the cabinet's language, never the browser's (design 29, W5).
  const cabinet = i18n.language;
  const named =
    cabinet === 'en'
      ? (name ?? englishName(code))
      : (nameIn(code, cabinet) ?? name ?? englishName(code));
  if (named === null) {
    return code;
  }
  const own = ownName(code);
  return own === null || own.toLocaleLowerCase(code) === named.toLocaleLowerCase(code)
    ? named
    : t('editor.setup.languageWithOwnName', { name: named, own });
}

/**
 * What the picker offers: the served set, in the platform's order, plus a stored code outside it -
 * an imported article's - so the article still shows what it is and the pick is not forced.
 */
export function sourceLanguageOptions(
  current: string | null,
  offered: readonly OfferedLanguage[],
  t: ArticlesT = articlesT(),
): readonly { readonly value: string; readonly label: string }[] {
  const options = offered.map(({ code, name }) => ({
    value: code,
    label: languageLabel(code, name, t),
  }));
  return current === null || offered.some(({ code }) => code === current)
    ? options
    : [...options, { value: current, label: languageLabel(current, null, t) }];
}

/**
 * The words an author reads for a code: the platform's name when the served set names it, else the
 * browser's, else the code itself. `offered` is empty where nothing was read - the History pane's
 * translation rows, whose languages are the translation targets and not this set.
 */
export function sourceLanguageLabel(
  code: string | null,
  offered: readonly OfferedLanguage[] = [],
  t: ArticlesT = articlesT(),
): string {
  if (code === null) {
    return t('editor.setup.languageNotSet');
  }
  return languageLabel(code, offered.find((language) => language.code === code)?.name ?? null, t);
}

/**
 * ★ THE LANGUAGE TO SEND, OR `undefined` TO SEND NONE. Only a CHANGE is sent: an absent member keeps
 * the stored value (`applySourceLanguage`, src/modules/articles/services/working-copy.repository.ts),
 * and a change once the article has a version is a 422 - so a whole-document autosave that always
 * carried the language would carry that refusal into every save after it.
 */
export function sourceLanguageChange(draft: EditorDraft, base: DraftBase): string | undefined {
  return draft.sourceLanguage !== null && draft.sourceLanguage !== base.savedSourceLanguage
    ? draft.sourceLanguage
    : undefined;
}

/**
 * Is this the autosave's 422 for the language? The platform answers an unoffered language, and a
 * change after the first version, with the uncoded 422 (authors-api.yaml; `UncodedUnprocessableError`).
 * There is no code to branch on, so the save that met it must have carried a language change for it
 * to count - and since wave 25c the same status also carries the address refusals, which DO have
 * codes (./address.ts), so those are told apart and never mistaken for a refused language.
 */
export function isSourceLanguageRefusal(error: unknown): boolean {
  return isApiProblem(error) && error.status === 422 && !isSlugRefusal(error);
}

/**
 * "1 word", "300 words": a count with its noun, singular for exactly one. The checklist's lines said
 * "1 categories." and "1 image(s)" until the owner's look at the editor (2026-10-05) - an author
 * reads one or many, never a bracket - and "At least 1 are needed" is the same mistake, so `band`
 * says "is" for a bound of one. Each form is the catalogue's plural, never an "s" added or trimmed.
 */
type CountedNoun = 'words' | 'blocks' | 'characters' | 'categories' | 'images';

function counted(count: number, noun: CountedNoun, t: ArticlesT): string {
  return t(`editor.checklist.counted.${noun}`, { count });
}

/** "12", or "12 of 300" where the policy sent a bound. */
function ofMax(value: number, max: number | null, t: ArticlesT): string {
  return max === null
    ? String(value)
    : t('editor.checklist.ofMax', { value: String(value), max: String(max) });
}

/**
 * ★ THE COVER ROW OF A PIECE THE AUTHOR CANNOT WRITE IS NEVER RED (the owner, 2026-10-05). "Needs
 * a cover" is shown only where the author can still act on it: `draft` and `changes_requested`, the
 * two statuses the platform answers `writableBy: 'author'` in. A piece that is with an editor,
 * scheduled, published, taken down or archived keeps the cover it has - the platform's own
 * `awaitingCover` is false there (`awaitingCoverIn`, src/modules/articles/services/
 * working-copy.presenter.ts), and this row agrees rather than measuring on its own. Imported pieces
 * are why: the old apps cropped every cover smaller than the size asked today, so every published
 * one was flagged over a cover only an editor could change. The row still says what the cover is -
 * its size, or that there is none - and never what it should be. A draft is measured as before, and
 * the size is still checked when it is sent.
 */
function coverAsItStands(
  rule: StructuralRuleName,
  label: string,
  minWidth: number | null,
  hasCover: boolean,
  base: DraftBase,
  t: ArticlesT,
): ChecklistRow {
  const width = base.coverWidthPx;
  const height = base.coverHeightPx;
  return {
    rule,
    label,
    min: minWidth,
    max: null,
    actual: hasCover ? width : null,
    state: 'ok',
    detail: !hasCover
      ? t('editor.checklist.noCover')
      : width === null || height === null
        ? t('editor.checklist.coverInPlace')
        : t('editor.checklist.coverSize', { width: String(width), height: String(height) }),
  };
}
